import { FC, useMemo } from 'react';
import { DropdownMenuDivider, DropdownMenuItem } from '@/components/UI/Dropdown';
import type { DropdownRenderAsChildProps } from '@/components/UI/Dropdown/Dropdown.model';
import { defaultPromiseErrorHandler, errorToast, successToast } from '@/helpers';
import { _isNotEmpty, _notNil } from '@/littledash';
import { useModalAction } from '@/utils/modal';
import {
  DataTableColumn,
  DataTableColumnAddRequest,
  DataTableColumnMoveRequest,
  DataTableColumnUpdateRequest,
  DataTableColumnUpdateV1SampleDoseTime,
  DataTablePkIntervalTimeUnit,
  DataTableService,
  isUpdatableColumnType,
} from '../DataTable.model';
import type { DataTableColumnFillRequestV1 } from '@/model/DataTable.model';
import { formulaVariablesFromColumnIds, isChildColumn, isFillableColumn } from '../DataTable.util';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import { calculateMenuFlags, Flag, hasFlag } from './ColumnMenu.utils';

interface ColumnMenuProps extends DropdownRenderAsChildProps {
  column: DataTableColumn | undefined;
  dataTableService: DataTableService;
  rowCount: number;
  testId?: string;
}

const ColumnMenu: FC<ColumnMenuProps> = ({ column, dataTableService, rowCount, testId = 'column-menu' }) => {
  const { openModal, closeModal } = useModalAction();

  const flags = useMemo(
    () => calculateMenuFlags(column, dataTableService.type, dataTableService.unit, rowCount),
    [column, dataTableService.type, dataTableService.unit, rowCount]
  );

  const handleMoveColumn = () => {
    openModal('MOVE_DATATABLE_COLUMN', {
      column,
      tableType: dataTableService.type,
      columns: dataTableService.columns,
      onSave: (update: DataTableColumnMoveRequest) => {
        dataTableService
          .moveColumn(update)
          .then(() => successToast('Column moved Successfully'))
          .then(() => dataTableService.validate())
          .then(() => dataTableService.validateColumn(update.column_id))
          .catch(defaultPromiseErrorHandler);
      },
    });
  };

  const handleFill = async () => {
    if (isFillableColumn(column)) {
      openModal('DATATABLE_COLUMN_FILL', {
        column,
        onFill: async (value: DataTableColumnFillRequestV1) =>
          dataTableService.fillColumn(column.id, value).then(closeModal),
        onCancel: () => {
          closeModal();
        },
      });
    }
  };

  const handleColumEdit = async () => {
    switch (column?.type) {
      case 'formula': {
        openModal('EDIT_DATATABLE_FORMULA_COLUMN', {
          formData: {
            name: column.name,
            unit: column.unit,
            formula: await formulaVariablesFromColumnIds(column.formula?.value ?? '', dataTableService.leafColumns, {
              ignoreInvalidVariables: true,
            }),
          },
          columnId: column.id,
          columns: dataTableService.leafColumns,
          onSubmit: (formData: DataTableColumnAddRequest) => {
            dataTableService
              .updateColumn({
                id: column.id,
                type: column.type,
                name: formData.name,
                unit: formData.unit,
                formula: formData.formula,
              })
              .then(() => successToast('Column updated'))
              .catch(() => errorToast('Could not update column'));
          },
        });
        break;
      }
      case 'timestampBaseline': {
        const from = DateInputUtils.toLocalDateTime(column.pk.from ?? undefined, { includeSeconds: false });
        openModal('EDIT_DATATABLE_TIMESTAMP_BASELINE_COLUMN', {
          formData: {
            from,
            name: column.name,
            interval: column.pk?.interval,
            interval_time_unit: column?.pk?.interval_time_unit,
            clash_window: column.pk?.clash_window ?? null,
          },
          readOnly: true,
          columnId: column.id,
          columns: dataTableService.leafColumns,
          onSubmit: (formData: DataTableColumnAddRequest) => {
            dataTableService
              .updateColumn({
                id: column.id,
                type: column.type,
                name: formData.name,
                clash_window: formData?.pk?.clash_window,
              })
              .then(() => successToast('Column updated'))
              .catch(() => errorToast('Could not update column'));
          },
        });
        break;
      }
      case 'timestampBaselineRelative': {
        openModal('EDIT_DATATABLE_TIMESTAMP_BASELINE_RELATIVE_COLUMN', {
          formData: {
            name: column.name,
            interval: column.pk?.interval,
            interval_time_unit: column?.pk?.interval_time_unit ?? null,
            timestamp_baseline_id: column.pk?.timestamp_baseline_id,
          },
          columnId: column.id,
          columns: dataTableService.leafColumns,
          onSubmit: (formData: DataTableColumnAddRequest) => {
            dataTableService
              .updateColumn({
                id: column.id,
                type: column.type,
                name: formData.name,
                pk: {
                  interval: formData?.pk?.interval,
                  interval_time_unit: formData?.pk?.interval_time_unit as
                    | DataTablePkIntervalTimeUnit
                    | null
                    | undefined,
                  timestamp_baseline_api_id: formData?.pk?.timestamp_baseline_id,
                },
              })
              .then(() => successToast('Column updated'))
              .catch(() => errorToast('Could not update column'));
          },
        });
        break;
      }
      case 'measurement': {
        openModal('EDIT_DATATABLE_MEASUREMENT_COLUMN', {
          formData: {
            referenceDate: column.measurement?.measured_at,
          },
          column,
          columns: dataTableService.leafColumns,
          onSave: (columnUpdate: DataTableColumnUpdateRequest) => {
            dataTableService
              .updateColumn(columnUpdate)
              .then(() => successToast('Column updated'))
              .catch(() => errorToast('Could not update column'));
          },
        });
        break;
      }
      case 'observation': {
        openModal('EDIT_DATATABLE_OBSERVATION_COLUMN', {
          formData: {
            referenceDate: column.observation?.observed_at,
          },
          column,
          columns: dataTableService.leafColumns,
          onSave: (columnUpdate: DataTableColumnUpdateRequest) => {
            dataTableService
              .updateColumn(columnUpdate)
              .then(() => successToast('Column updated'))
              .catch(() => errorToast('Could not update column'));
          },
        });
        break;
      }
      case 'sampleDoseTime': {
        openModal('EDIT_DATATABLE_SAMPLE_DOSE_TIME_COLUMN', {
          column,
          onSave: (columnUpdate: DataTableColumnUpdateV1SampleDoseTime) =>
            dataTableService
              .updateColumn({ ...columnUpdate, id: column.id })
              .then(() => successToast('Column updated'))
              .catch(() => errorToast('Could not update column')),
        });
        break;
      }
      case 'text':
      case 'number':
      case 'timestamp': {
        openModal('EDIT_DATATABLE_COLUMN', {
          tableType: dataTableService.type,
          column,
          columns: dataTableService.leafColumns,
          onSave: (columnUpdate: DataTableColumnUpdateRequest) =>
            dataTableService
              .updateColumn(columnUpdate)
              .then(() => successToast('Column updated'))
              .catch(() => errorToast('Could not update column')),
        });
        break;
      }
    }
  };

  const handleColumDelete = () => {
    const columnGroupingData = dataTableService.getColumnGroupingData();
    const childColumns =
      columnGroupingData?.leafColumns.filter((col) => isChildColumn(col) && col.parent_api_id === column?.id) ?? [];
    const isParentColumn = _isNotEmpty(childColumns);

    let textOverride: string | undefined = undefined;
    if (column?.type === 'timestampBaseline') {
      textOverride =
        'Deleting this Timestamp: PK Dose column will delete all Timestamp: PK Bleed columns related to it.';
    } else if (isParentColumn) {
      textOverride = 'Deleting this parent column will delete all child columns grouped under it.';
    }

    openModal('CONFIRM_DELETE_DATA_TABLE_COLUMN', {
      textOverride,
      onClick: () => {
        const columnId = column?.id;
        if (_notNil(columnId)) {
          dataTableService
            .removeColumn(columnId)
            .then(() => {
              if (column?.type === 'timestampBaselineRelative' || column?.type === 'timestampBaseline') {
                dataTableService.validate();
              }
            })
            .then(() => successToast('Column removed'))
            .catch(defaultPromiseErrorHandler);
        }
      },
      deleteManyColumns:
        column?.type === 'measurement'
          ? () => {
              const columnList: Array<DataTableColumn> | null = dataTableService?.columnsByIds(
                column?.relations ?? undefined
              );
              if (_isNotEmpty(columnList)) {
                return (
                  <div>
                    <span>This column and its dependent columns will be permanently deleted:</span>
                    <ul className="overflow-scroll mt3 ba b--gray" style={{ maxHeight: '150px' }}>
                      {columnList?.map((object, index) => {
                        const referenceDate =
                          object.type === 'measurement' || object.type === 'observation'
                            ? object.reference_date
                            : undefined;
                        return (
                          <li
                            className="bg-light-gray pv2 pl2 tl ba b--moon-gray lh-title f6 near-black"
                            key={index}
                          >{`${object.name ?? ''} (${DateUtils.renderDate(referenceDate, {
                            defaultResponse: '',
                          })})`}</li>
                        );
                      })}
                    </ul>
                  </div>
                );
              }
              return null;
            }
          : isParentColumn
            ? () => (
                <div>
                  <span>This parent column and all its child columns will be permanently deleted:</span>
                  <ul className="overflow-scroll mt3 ba b--gray" style={{ maxHeight: '150px' }}>
                    {childColumns.map((childColumn, index) => (
                      <li className="bg-light-gray pv2 pl2 tl ba b--moon-gray lh-title f6 near-black" key={index}>
                        {childColumn.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            : null,
    });
  };

  const handleColumnLock = (locked: boolean) => {
    if (_notNil(column) && isUpdatableColumnType(column.type)) {
      dataTableService
        .updateColumn({ id: column.id, type: column.type, locked })
        .then(() => successToast(`Column ${locked ? 'locked' : 'unlocked'}`))
        .catch(() => errorToast(`Could not ${locked ? 'lock' : 'unlock'} column`));
    }
  };

  const displayMove = hasFlag(flags, Flag.display_move);
  const displayEdit = hasFlag(flags, Flag.display_edit);
  const displayFill = hasFlag(flags, Flag.display_fill);
  const displayLock = hasFlag(flags, Flag.display_lock);
  const displayDelete = hasFlag(flags, Flag.display_delete);

  const moveActive = hasFlag(flags, Flag.move_active);
  const editActive = hasFlag(flags, Flag.edit_active);
  const fillActive = hasFlag(flags, Flag.fill_active);
  const deleteActive = hasFlag(flags, Flag.delete_active);

  const isLocked = hasFlag(flags, Flag.locked);

  return (
    <span data-test-component="ColumnMenu" data-testid={testId}>
      {displayMove && (
        <DropdownMenuItem onClick={handleMoveColumn} disabled={!moveActive}>
          <span>Move column</span>
        </DropdownMenuItem>
      )}
      {displayEdit && (
        <DropdownMenuItem disabled={!editActive} onClick={() => handleColumEdit()}>
          <span>Edit</span>
        </DropdownMenuItem>
      )}
      {displayFill && (
        <DropdownMenuItem disabled={!fillActive} onClick={() => handleFill()}>
          {column?.type === 'sampleCollectedAt' ? <span>Collect all</span> : <span>Fill</span>}
        </DropdownMenuItem>
      )}
      {displayLock && (
        <ColumLockMenuItem locked={isLocked} read_only={column?.read_only ?? false} onClick={handleColumnLock} />
      )}
      <DropdownMenuDivider />
      {displayDelete && (
        <DropdownMenuItem onClick={handleColumDelete} disabled={!deleteActive}>
          <span className="red">Delete</span>
        </DropdownMenuItem>
      )}
    </span>
  );
};

const ColumLockMenuItem: FC<
  {
    onClick: (locked: boolean) => void;
  } & Required<Pick<DataTableColumn, 'locked' | 'read_only'>>
> = ({ locked, read_only, onClick }) => {
  if (read_only && !locked) {
    return null;
  }
  if (locked) {
    return (
      <DropdownMenuItem onClick={() => onClick(false)}>
        <span>Unlock column</span>
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem onClick={() => onClick(true)}>
      <span>Lock column</span>
    </DropdownMenuItem>
  );
};

export default ColumnMenu;
